
import { UserProfile } from "@/types/profile";
import { TouchOptimizedCard } from "@/components/ui/touch-optimized-card";
import { Card, CardContent } from "@/components/ui/card";
import { Banknote, Calendar, Ticket, Clock } from "lucide-react";
import { useResponsiveLayout } from "@/hooks/useResponsiveLayout";
import { AdaptiveGrid } from "@/components/ui/adaptive-grid";

type QuickStatsProps = {
  profile: UserProfile | null;
};

export const QuickStats = ({ profile }: QuickStatsProps) => {
  const { isMobile, screenSize } = useResponsiveLayout();
  
  // Only show relevant stats based on user roles
  const showRevenue = profile?.role_types?.some(role => 
    ['Venue Owner', 'Promoter', 'DJ', 'Performer'].includes(role)
  );
  const showTickets = profile?.role_types?.some(role => 
    ['Venue Owner', 'Promoter'].includes(role)
  );
  const showGigs = profile?.role_types?.some(role => 
    ['DJ', 'Performer'].includes(role)
  );
  const showShifts = profile?.role_types?.some(role => 
    ['Bartender', 'Security'].includes(role)
  );

  // Create stats array for easier mobile handling
  const stats = [
    showRevenue && {
      title: "Revenue",
      value: "$0.00",
      icon: Banknote,
      color: "green"
    },
    showTickets && {
      title: "Tickets Sold",
      value: "0",
      icon: Ticket,
      color: "purple"
    },
    showGigs && {
      title: "Next Gig",
      value: "-",
      icon: Calendar,
      color: "blue"
    },
    showShifts && {
      title: "Next Shift",
      value: "-",
      icon: Clock,
      color: "pink"
    }
  ].filter(Boolean);

  // Color classes map
  const colorClasses = {
    green: {
      bgGlow: "bg-green-500/5",
      gradient: "from-green-500/20 to-green-700/20",
      border: "border-green-500/20",
      text: "text-green-400"
    },
    purple: {
      bgGlow: "bg-purple-500/5",
      gradient: "from-purple-500/20 to-purple-700/20",
      border: "border-purple-500/20",
      text: "text-purple-400"
    },
    blue: {
      bgGlow: "bg-blue-500/5",
      gradient: "from-blue-500/20 to-blue-700/20",
      border: "border-blue-500/20",
      text: "text-blue-400"
    },
    pink: {
      bgGlow: "bg-pink-500/5",
      gradient: "from-pink-500/20 to-pink-700/20",
      border: "border-pink-500/20",
      text: "text-pink-400"
    }
  };

  if (stats.length === 0) {
    return null;
  }

  return (
    <div className="animate-slide-up" style={{animationDelay: "200ms"}}>
      {isMobile ? (
        <div className="overflow-x-auto -mx-4 px-4 pb-2">
          <div className="flex space-x-3" style={{minWidth: "max-content"}}>
            {stats.map((stat, index) => {
              if (!stat) return null;
              const StatIcon = stat.icon;
              const colors = colorClasses[stat.color as keyof typeof colorClasses];
              
              return (
                <Card key={index} className="glass-card border-border/30 overflow-hidden w-[160px] flex-shrink-0 group hover:scale-105 transition-all duration-300">
                  <CardContent className="p-3 relative">
                    <div className={`absolute -right-2 -bottom-2 w-16 h-16 rounded-full ${colors.bgGlow} blur-lg transition-all duration-300 group-hover:blur-xl`}></div>
                    <div className="relative z-10 space-y-2">
                      <div className={`p-2 rounded-xl bg-gradient-to-br ${colors.gradient} backdrop-blur-sm border ${colors.border} w-fit`}>
                        <StatIcon className={`h-4 w-4 ${colors.text}`} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground leading-tight">{stat.title}</p>
                        <p className="text-lg font-bold text-foreground">{stat.value}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
        <AdaptiveGrid 
          adaptiveColumns={{ mobile: 1, tablet: 2, laptop: 3, desktop: 4 }}
          gap="md"
        >
          {showRevenue && (
            <Card className="glass-morphism border-none overflow-hidden hover-lift">
              <CardContent className="p-4 flex items-center gap-4 relative">
                <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-green-500/5 blur-xl"></div>
                <div className="p-3 rounded-full bg-gradient-to-br from-green-500/20 to-green-700/20 backdrop-blur-sm border border-green-500/20">
                  <Banknote className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-bold">$0.00</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {showTickets && (
            <Card className="glass-morphism border-none overflow-hidden hover-lift">
              <CardContent className="p-4 flex items-center gap-4 relative">
                <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-purple-500/5 blur-xl"></div>
                <div className="p-3 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-700/20 backdrop-blur-sm border border-purple-500/20">
                  <Ticket className="h-6 w-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tickets Sold</p>
                  <p className="text-2xl font-bold">0</p>
                </div>
              </CardContent>
            </Card>
          )}

          {showGigs && (
            <Card className="glass-morphism border-none overflow-hidden hover-lift">
              <CardContent className="p-4 flex items-center gap-4 relative">
                <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-blue-500/5 blur-xl"></div>
                <div className="p-3 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-700/20 backdrop-blur-sm border border-blue-500/20">
                  <Calendar className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Next Gig</p>
                  <p className="text-2xl font-bold">-</p>
                </div>
              </CardContent>
            </Card>
          )}

          {showShifts && (
            <Card className="glass-morphism border-none overflow-hidden hover-lift">
              <CardContent className="p-4 flex items-center gap-4 relative">
                <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-pink-500/5 blur-xl"></div>
                <div className="p-3 rounded-full bg-gradient-to-br from-pink-500/20 to-pink-700/20 backdrop-blur-sm border border-pink-500/20">
                  <Clock className="h-6 w-6 text-pink-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Next Shift</p>
                  <p className="text-2xl font-bold">-</p>
                </div>
              </CardContent>
            </Card>
          )}
        </AdaptiveGrid>
      )}
    </div>
  );
};
